(function () {
  'use strict';

  var VERSION = '0.1.2-beta';
  var REWARD_ATTRIBUTE = '_tier_reward_amount';
  var FIFTEEN_THRESHOLD = 10000;
  var TWENTY_FIVE_THRESHOLD = 15000;
  var EROS_GIFT_VARIANT_ID = 43947866915013;
  var EROS_GIFT_PROPERTY = '_free_gift';
  var EROS_GIFT_CODE = 'EROS-FREESB';

  var refreshTimer = null;
  var refreshing = false;
  var refreshQueued = false;
  var syncInFlight = false;
  var lastState = null;

  function getCartUrl() {
    var url = window.theme && theme.routes && theme.routes.cart
      ? theme.routes.cart
      : '/cart.js';

    if (!/\.js(\?|$)/.test(url)) {
      url = url.replace(/\/?$/, '') + '.js';
    }

    return url + (url.indexOf('?') === -1 ? '?' : '&') + 't=' + Date.now();
  }

  function getCartUpdateUrl() {
    var root = window.Shopify && Shopify.routes && Shopify.routes.root
      ? Shopify.routes.root
      : '/';

    return root.replace(/\/?$/, '/') + 'cart/update.js';
  }

  function getCart() {
    return window.fetch(getCartUrl(), {
      method: 'GET',
      credentials: 'same-origin',
      headers: {
        'Accept': 'application/json'
      }
    }).then(function (response) {
      if (!response.ok) {
        throw new Error('Unable to load the cart.');
      }

      return response.json();
    });
  }

  function isExcludedItem(item) {
    if (!item) {
      return false;
    }

    if (item.gift_card) {
      return true;
    }

    return Number(item.variant_id || item.id) === EROS_GIFT_VARIANT_ID &&
      item.properties &&
      String(item.properties[EROS_GIFT_PROPERTY] || '').toUpperCase() === EROS_GIFT_CODE;
  }

  function getQualifyingSubtotal(cart) {
    var subtotal = Number(cart && cart.original_total_price) || 0;

    if (!cart || !Array.isArray(cart.items)) {
      return Math.max(0, subtotal);
    }

    cart.items.forEach(function (item) {
      if (isExcludedItem(item)) {
        subtotal -= Number(item.original_line_price) || 0;
      }
    });

    return Math.max(0, subtotal);
  }

  function getAppliedDiscountCodes(cart) {
    var codes = [];

    function addCode(code) {
      var normalizedCode = code && String(code);

      if (!normalizedCode || codes.some(function (existingCode) {
        return existingCode.toUpperCase() === normalizedCode.toUpperCase();
      })) {
        return;
      }

      codes.push(normalizedCode);
    }

    (cart.discount_codes || []).forEach(function (discount) {
      if (discount.applicable !== false) {
        addCode(discount.code);
      }
    });

    (cart.cart_level_discount_applications || []).forEach(function (application) {
      if (application.type === 'discount_code') {
        addCode(application.title || application.code);
      }
    });

    (cart.items || []).forEach(function (item) {
      (item.line_level_discount_allocations || []).forEach(function (allocation) {
        var application = allocation.discount_application || {};

        if (application.type === 'discount_code') {
          addCode(application.title || application.code);
        }
      });
    });

    return codes;
  }

  function getRewardAmount(subtotal) {
    if (subtotal >= TWENTY_FIVE_THRESHOLD) {
      return 25;
    }

    if (subtotal >= FIFTEEN_THRESHOLD) {
      return 15;
    }

    return 0;
  }

  function saveState(cart, subtotal, discountCodes, rewardAmount) {
    lastState = {
      cartToken: cart.token || null,
      itemCount: Number(cart.item_count) || 0,
      qualifyingSubtotal: subtotal,
      discountCodes: discountCodes.slice(),
      rewardAmount: rewardAmount,
      cartAttribute: String(
        (cart.attributes && cart.attributes[REWARD_ATTRIBUTE]) || '0'
      ),
      updatedAt: new Date().toISOString()
    };
  }

  function syncRewardAttribute(cart) {
    var subtotal = getQualifyingSubtotal(cart);
    var discountCodes = getAppliedDiscountCodes(cart);
    var rewardAmount = getRewardAmount(subtotal);
    var currentValue = String(
      (cart.attributes && cart.attributes[REWARD_ATTRIBUTE]) || '0'
    );
    var nextValue = String(rewardAmount);

    saveState(cart, subtotal, discountCodes, rewardAmount);

    if (syncInFlight || currentValue === nextValue) {
      return Promise.resolve(cart);
    }

    var payload = { attributes: {} };
    payload.attributes[REWARD_ATTRIBUTE] = nextValue;
    syncInFlight = true;

    return window.fetch(getCartUpdateUrl(), {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(payload)
    }).then(function (response) {
      if (!response.ok) {
        throw new Error('Unable to update the tier reward cart attribute.');
      }

      return response.json();
    }).then(function (updatedCart) {
      syncInFlight = false;
      saveState(updatedCart, subtotal, discountCodes, rewardAmount);
      return updatedCart;
    }).catch(function (error) {
      syncInFlight = false;
      console.warn('Silent tier reward synchronization failed.', error);
      throw error;
    });
  }

  function refresh() {
    if (refreshing) {
      refreshQueued = true;
      return;
    }

    refreshing = true;

    getCart()
      .then(syncRewardAttribute)
      .catch(function (error) {
        console.warn('Silent tier reward calculation skipped.', error);
      })
      .finally(function () {
        refreshing = false;

        if (refreshQueued) {
          refreshQueued = false;
          scheduleRefresh(150);
        }
      });
  }

  function scheduleRefresh(delay) {
    window.clearTimeout(refreshTimer);
    refreshTimer = window.setTimeout(refresh, delay == null ? 500 : delay);
  }

  function getRequestUrl(input) {
    if (typeof input === 'string') {
      return input;
    }

    return input && input.url ? input.url : '';
  }

  function isCartMutationUrl(url) {
    return /\/cart\/(add|change|update|clear)(\.js)?(?:[?#]|$)/i.test(url || '');
  }

  function observeFetchCartChanges() {
    if (!window.fetch || window.fetch.__tierRewardsBetaObserver) {
      return;
    }

    var originalFetch = window.fetch;

    function observedFetch(input) {
      var requestUrl = getRequestUrl(input);
      var request = originalFetch.apply(this, arguments);

      if (!isCartMutationUrl(requestUrl)) {
        return request;
      }

      return request.then(function (response) {
        scheduleRefresh(500);
        return response;
      });
    }

    observedFetch.__tierRewardsBetaObserver = true;
    window.fetch = observedFetch;
  }

  function observeXhrCartChanges() {
    if (!window.XMLHttpRequest || XMLHttpRequest.prototype.__tierRewardsBetaObserver) {
      return;
    }

    var originalOpen = XMLHttpRequest.prototype.open;
    var originalSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (method, url) {
      this.__tierRewardsBetaCartMutation = isCartMutationUrl(String(url || ''));
      return originalOpen.apply(this, arguments);
    };

    XMLHttpRequest.prototype.send = function () {
      if (this.__tierRewardsBetaCartMutation) {
        this.addEventListener('loadend', function () {
          scheduleRefresh(500);
        }, { once: true });
      }

      return originalSend.apply(this, arguments);
    };

    XMLHttpRequest.prototype.__tierRewardsBetaObserver = true;
  }

  function initialize() {
    observeFetchCartChanges();
    observeXhrCartChanges();
    refresh();
  }

  [
    'cart:updated',
    'cart:build',
    'cart:refresh',
    'ajaxProduct:added',
    'product:added'
  ].forEach(function (eventName) {
    document.addEventListener(eventName, function () {
      scheduleRefresh(350);
    });
  });

  window.addEventListener('pageshow', function () {
    scheduleRefresh(150);
  });

  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') {
      scheduleRefresh(250);
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize);
  } else {
    initialize();
  }

  window.cartTierRewardsBetaStatus = {
    installed: true,
    version: VERSION,
    refresh: refresh,
    getState: function () {
      return lastState;
    }
  };
})();
